/**
 * Understanding result. Mini PRD: classification, plain-English summary,
 * what it means for the carer's situation. Every results screen carries the
 * CAM escalation route and the "this is wrong" guardrail control.
 *
 * Note: the summary is deliberately not editable - the Mini PRD scopes the
 * review gate to actions, not to rewriting the understanding.
 */
import { useEffect } from 'react';
import { useNavigation } from '@/hooks/useNavigationContext';
import { Box, Stack, Typography, Divider } from '@mui/material';

import { Card } from '@karehero/llama/components/atoms/Card/Card';
import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';

import FlowScreen from './components/FlowScreen';
import { FlagIcon } from './components/LocalIcons';
import { palette } from './components/palette';
import { useCaptureFlow } from './CaptureFlowContext';

const Bullets = ({ items }: { items: string[] }) => (
  <Stack spacing={1}>
    {items.map((item, i) => (
      <Stack
        sx={{ alignItems: 'flex-start' }}
        key={i}
        direction='row'
        spacing={1}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: palette.forest,
            mt: '7px',
            flexShrink: 0,
          }}
        />
        <Typography sx={{ fontSize: 14 }}>{item}</Typography>
      </Stack>
    ))}
  </Stack>
);

export const Result = () => {
  const navigate = useNavigation();
  const { doc, method, actions, markedWrong, markWrong, saveCapture } =
    useCaptureFlow();

  useEffect(() => {
    if (!doc) navigate('/capture', { replace: true });
  }, [doc, navigate]);

  if (!doc) return null;

  const methodLabel =
    method === 'photo'
      ? `${doc.pageCount} page${doc.pageCount === 1 ? '' : 's'} photographed`
      : method === 'gallery'
        ? 'From your photos'
        : 'Uploaded file';

  const saveWithoutActions = () => {
    saveCapture(false);
    navigate('/');
  };

  return (
    <FlowScreen backTo='/care'>
      <Stack spacing={2.5}>
        <PageHeader
          title='Here’s what Keni found'
          description='Check everything looks right before you save.'
        />

        {/* Honest labelling for tester-supplied files (no live pipeline yet) */}
        {doc.isUserProvided && (
          <Box
            sx={{
              backgroundColor: palette.softYellow,
              borderRadius: 2,
              padding: 1.5,
            }}
          >
            <Typography sx={{ fontSize: 12 }}>
              Demo mode: this is your real file, but the understanding below is
              a stand-in. The live pipeline reads actual content.
            </Typography>
          </Box>
        )}

        {/* Document row */}
        <Card isFullWidth isPaddingSm>
          <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
            <Icon icon='file-lines' fontSize={22} />
            <Stack sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 14 }}>{doc.fileName}</Typography>
              <Typography sx={{ color: palette.mutedText, fontSize: 12 }}>
                {doc.fileSizeKb}kb · {methodLabel} · Captured just now
              </Typography>
            </Stack>
          </Stack>
        </Card>

        {/* Classification */}
        <Stack spacing={0.5}>
          <Typography
            sx={{
              fontSize: 12,

              color: palette.mutedText,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            This looks like
          </Typography>
          <Typography variant='h5'>{doc.classification}</Typography>
          {/* Tiering: deep-treatment types get richer, type-specific guidance */}
          {doc.tier === 'deep' && (
            <Box
              sx={{
                alignSelf: 'flex-start',
                backgroundColor: palette.lavender,
                color: palette.lavenderInk,
                borderRadius: 99,
                padding: '3px 10px',
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              Priority document type · Keni knows these well
            </Box>
          )}
        </Stack>

        <Divider sx={{ borderColor: palette.divider }} />

        <Stack spacing={1}>
          <Typography variant='h6'>What it says</Typography>
          <Bullets items={doc.summary} />
        </Stack>

        <Stack spacing={1}>
          <Typography variant='h6'>What this means for you</Typography>
          <Bullets items={doc.meaning} />
        </Stack>

        {/* Guardrail: the "this is wrong" control, tracked as a rate */}
        {!markedWrong ? (
          <Box
            component='button'
            onClick={markWrong}
            sx={{
              alignSelf: 'flex-start',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: palette.mutedText,
              fontSize: 13,
              textDecoration: 'underline',
              padding: 0,
            }}
          >
            <FlagIcon size={14} />
            Something here is wrong
          </Box>
        ) : (
          <Box
            sx={{
              backgroundColor: '#fce9e5',
              borderRadius: 2,
              padding: 2,
            }}
          >
            <Stack sx={{ alignItems: 'flex-start' }} spacing={1}>
              <Typography sx={{ fontSize: 14 }}>
                Thanks - we've logged that
              </Typography>
              <Typography sx={{ fontSize: 13 }}>
                Keni won't suggest actions from this document, and your care
                manager can help you with it directly.
              </Typography>
              <Button
                ariaLabel='Talk to your care manager'
                variant='secondary'
                onPress={() => navigate('/help')}
              >
                Talk to your care manager
              </Button>
            </Stack>
          </Box>
        )}

        {/* CAM escalation: on every results screen, per the safety posture */}
        <Card isFullWidth isPaddingSm>
          <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
            <Icon icon='user-headset' fontSize={22} />
            <Stack sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 14 }}>
                Not sure about any of this?
              </Typography>
              <Typography sx={{ color: palette.mutedText, fontSize: 12 }}>
                Your care manager can talk it through with you.
              </Typography>
            </Stack>
            <Button
              ariaLabel='Ask your care manager'
              variant='link'
              onPress={() => navigate('/help')}
            >
              Ask
            </Button>
          </Stack>
        </Card>

        {!markedWrong && (
          <Stack spacing={1.5} sx={{ pb: 2 }}>
            <Button
              ariaLabel='Review suggested next steps'
              variant='primary'
              isFullWidth
              onPress={() => navigate('/capture/review')}
            >
              {`Review suggested next steps (${actions.length})`}
            </Button>
            <Button
              ariaLabel='Save document only'
              variant='link'
              isFullWidth
              onPress={saveWithoutActions}
            >
              Save document only
            </Button>
          </Stack>
        )}
        {markedWrong && (
          <Button
            ariaLabel='Back to Care'
            variant='secondary'
            isFullWidth
            onPress={() => navigate('/care')}
          >
            Done
          </Button>
        )}
      </Stack>
    </FlowScreen>
  );
};

export default Result;
